/**
 * Middle pane — the message thread and the streaming composer. Enter sends, Shift+Enter
 * inserts a newline; the composer disables while a reply is streaming.
 */

import { Loader2, Send } from "lucide-react";
import { type KeyboardEvent, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";

import { type ChatMessage, MessageBubble } from "./message-bubble";

interface ThreadProps {
  messages: ChatMessage[];
  statusText: string | null;
  errorMessage: string | null;
  sending: boolean;
  disabled: boolean;
  disabledReason: string | null;
  showToolCalls: boolean;
  onSend: (content: string) => void;
}

export function Thread({
  messages,
  statusText,
  errorMessage,
  sending,
  disabled,
  disabledReason,
  showToolCalls,
  onSend,
}: ThreadProps) {
  const [draft, setDraft] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages, statusText]);

  function submit() {
    const content = draft.trim();
    if (!content || sending || disabled) {
      return;
    }
    setDraft("");
    onSend(content);
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        {messages.length === 0 ? (
          <div className="text-muted-foreground flex h-full items-center justify-center text-center text-sm">
            <p className="max-w-sm">
              Ask about your SKU portfolio — problematic SKUs, capacity scenarios, non-movers,
              anything the field or the capacity screen can answer. The agent cites the data it used
              and volunteers charts as evidence.
            </p>
          </div>
        ) : (
          messages.map((m) => (
            <MessageBubble key={m.id} message={m} showToolCalls={showToolCalls} />
          ))
        )}
        {statusText ? (
          <p className="text-muted-foreground flex items-center gap-1.5 text-xs italic">
            <Loader2 className="h-3 w-3 animate-spin" /> {statusText}
          </p>
        ) : null}
        {errorMessage ? (
          <div className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm">
            {errorMessage}
          </div>
        ) : null}
        <div ref={bottomRef} />
      </div>
      <div className="border-border shrink-0 border-t p-3">
        {disabled && disabledReason ? (
          <p className="text-muted-foreground mb-2 text-xs">{disabledReason}</p>
        ) : null}
        <div className="flex items-end gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={sending || disabled}
            rows={2}
            placeholder="Ask the agent…"
            className="border-input bg-card text-foreground placeholder:text-muted focus-visible:ring-ring flex-1 resize-none rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          />
          <Button
            size="icon"
            onClick={submit}
            disabled={sending || disabled || !draft.trim()}
            aria-label="Send message"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
